import {
  AuthFill,
  Button,
  CompetitionQuestion,
  DateOfBirthMask,
  Email,
  FirstName,
  LastName,
  MarketingConsent,
  Postcode,
} from '@components/form/fields'
import { FormStep, FormWrapper } from '@components/form/layout'
import { Nav } from '@components/ui'
import { yupResolver } from '@hookform/resolvers/yup'
import { ERROR_MESSAGE } from '@utils/constants'
import { closeOverlay } from '@utils/helpers/index-client'
import { getContentFile, getPageData } from '@utils/helpers/index-server'
import { formEvent } from '@utils/helpers/tracking'
import { AygoXJuicyCoutureCompetitionSchema } from '@utils/schemas'
import axios from 'axios'
import type { GetServerSideProps } from 'next'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

// Correct answers per acceptance criteria on TGBWEB-18246: Q1 T4, Q2 Madonna, Q3 Sound of the Underground
const CORRECT_ANSWERS = {
  question_1: 'B',
  question_2: 'C',
  question_3: 'B',
}

const QUESTIONS = [
  {
    name: 'question_1',
    question:
      'In 2006, Toyota Aygo became the sponsor of which iconic UK TV show?',
    options: [
      { label: 'A) Soccer AM', value: 'A' },
      { label: 'B) T4', value: 'B' },
      { label: 'C) SMTV', value: 'C' },
      { label: 'D) Hollyoaks', value: 'D' },
    ],
  },
  {
    name: 'question_2',
    question:
      'In 2001, Juicy Couture designed their first custom velour tracksuit, sparking the loungewear trend worldwide. Which celebrity was this tracksuit custom-designed for?',
    options: [
      { label: 'A) Paris Hilton', value: 'A' },
      { label: 'B) Britney Spears', value: 'B' },
      { label: 'C) Madonna', value: 'C' },
      { label: 'D) Lindsay Lohan', value: 'D' },
    ],
  },
  {
    name: 'question_3',
    question:
      "Girls Aloud were formed on ITV's Popstars: The Rivals in late 2002. What was the exact title of their debut single that secured the UK Christmas #1 spot that year?",
    options: [
      { label: 'A) Love Machine', value: 'A' },
      { label: 'B) Sound of the Underground', value: 'B' },
      { label: 'C) Biology', value: 'C' },
      { label: 'D) Jump', value: 'D' },
    ],
  },
]

const AygoXJuicyCoutureCompetition = ({ content, tracking, brand, query }) => {
  const [submitResponse, setSubmitResponse] = useState('')

  const initialValues = {
    type: 'email',
    first_name: '',
    last_name: '',
    email: '',
    dob: '',
    postcode: '',
    question_1: '',
    question_2: '',
    question_3: '',
    notes: '',
    consent: {
      user: false,
    },
    response_code: content.hidden_fields?.response_code,
    campaign_response_code: content.hidden_fields?.campaign_response_code,
    campaign_code: content.hidden_fields?.campaign_code,
    is_lexus: brand === 'lexus',
  }

  const methods = useForm({
    resolver: yupResolver(AygoXJuicyCoutureCompetitionSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  const onSubmit = async (data) => {
    setSubmitResponse('')
    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/prize-draw`,
      headers: {
        'Content-Type': 'application/json',
      },
      data,
    })
      .then(() => {
        formEvent(
          {
            conversion: 1,
            name: tracking.form,
            step: 'form-submit',
            variant: '',
            page: tracking.page,
          },
          brand,
          data.email,
        )
        closeOverlay(
          content.confirmation.title,
          content.confirmation.copy,
          setSubmitResponse,
        )
      })
      .catch(() => {
        formEvent(
          {
            conversion: 0,
            name: tracking.form,
            step: 'form-error',
            variant: '',
            page: tracking.page,
          },
          brand,
        )
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  const parseNotesData = (data) => {
    const dob = methods.getValues('dob')
    const q1 = methods.getValues('question_1')
    const q2 = methods.getValues('question_2')
    const q3 = methods.getValues('question_3')

    const isAllCorrect =
      q1 === CORRECT_ANSWERS.question_1 &&
      q2 === CORRECT_ANSWERS.question_2 &&
      q3 === CORRECT_ANSWERS.question_3

    const notes = `${dob} | Q1: ${q1} | Q2: ${q2} | Q3: ${q3} | ${
      isAllCorrect ? 'Correct' : 'Incorrect'
    }`

    methods.setValue('notes', notes)
    onSubmit({ ...data, notes })
  }

  return (
    <>
      <Nav
        title={content.title}
        logoType={content?.logotype}
        customLogo={content?.customlogo}
      />
      <FormWrapper>
        <FormStep
          title={content.details.title}
          subtitle={content.details.subtitle}
          step={1}
          active={true}
        >
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(parseNotesData)}>
              <AuthFill />
              <FirstName />
              <LastName />
              <Email />
              <DateOfBirthMask />
              <Postcode />
              {QUESTIONS.map((q) => (
                <CompetitionQuestion
                  key={q.name}
                  name={q.name}
                  question={q.question}
                  options={q.options}
                />
              ))}
              <MarketingConsent />
              <Button submitResponse={submitResponse}>
                {content.details.submit}
              </Button>
            </form>
          </FormProvider>
        </FormStep>
      </FormWrapper>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'aygo-x-juicy-couture-competition'
  const [content, settings] = getContentFile(brand, `forms/${form}`)

  // if no content/disabled - show 404
  if (!content || !content.attributes?.active) {
    return {
      notFound: true,
    }
  }

  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }

  return {
    props: {
      content: content.attributes,
      title: content.attributes.title,
      tracking: {
        page: content.attributes.tracking?.page,
        form: content.attributes.tracking?.form,
        variant: '',
        initialStep: 'contact-details',
      },
      description: '',
      ...(await getPageData(context)),
    },
  }
}

export default AygoXJuicyCoutureCompetition
